import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'

import type { ShopPostStatus } from '@/lib/api/shop-cms-api'
import type { ShopPageKind } from '@/lib/api/shop-pages-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { pageKindLabel } from '@/components/retail/cms/page-form-model'
import { postStatusLabel } from '@/components/retail/cms/post-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useDebounced } from '@/hooks/use-debounced'
import { useShopPages } from '@/hooks/use-shop-pages-query'
import { m } from '@/lib/paraglide/messages'

// Bán lẻ › Trang: trang tĩnh website (giới thiệu, chính sách…) và câu chuyện
// sản phẩm (gắn vào nhóm qua "Câu chuyện" ở màn nhóm).
export const Route = createFileRoute('/_app/_authed/_sidebar/retail/pages/')({
    component: PagesPage,
})

const ALL = 'all'
const PER_PAGE = 20
const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('vi-VN') : '—'

export function PagesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_content.write')
    const [q, setQ] = useState('')
    const [kind, setKind] = useState(ALL)
    const [status, setStatus] = useState(ALL)
    const [page, setPage] = useState(1)
    const search = useDebounced(q.trim(), 300)
    const query = useShopPages({
        q: search || undefined,
        kind: kind === ALL ? undefined : (kind as ShopPageKind),
        status: status === ALL ? undefined : (status as ShopPostStatus),
        page,
        per_page: PER_PAGE,
    })
    const rows = query.data?.data ?? []
    const totalPages = query.data?.meta?.total_pages ?? 1
    const reset = (fn: (v: string) => void) => (v: string) => {
        fn(v)
        setPage(1)
    }

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.cmsPage_list_title()}
                subtitle={m.cmsPage_list_subtitle()}
                primaryActions={
                    canWrite ? (
                        <div className="flex gap-2">
                            <Button size="sm" asChild>
                                <Link
                                    to="/retail/pages/new"
                                    search={{ kind: 'page' }}
                                >
                                    <Plus className="mr-1 size-4" />
                                    {m.cmsPage_add_page()}
                                </Link>
                            </Button>
                            <Button size="sm" variant="outline" asChild>
                                <Link
                                    to="/retail/pages/new"
                                    search={{ kind: 'story' }}
                                >
                                    <Plus className="mr-1 size-4" />
                                    {m.cmsPage_add_story()}
                                </Link>
                            </Button>
                        </div>
                    ) : undefined
                }
            />
            <div className="flex flex-wrap items-end gap-3">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-8"
                        value={q}
                        aria-label={m.cmsPost_search()}
                        placeholder={m.cmsPost_search()}
                        onChange={(e) => reset(setQ)(e.target.value)}
                    />
                </div>
                <Select value={kind} onValueChange={reset(setKind)}>
                    <SelectTrigger
                        className="w-44"
                        aria-label={m.cmsPage_filter_kind()}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>
                            {m.cmsPage_filter_all_kinds()}
                        </SelectItem>
                        <SelectItem value="page">
                            {m.cmsPage_kind_page()}
                        </SelectItem>
                        <SelectItem value="story">
                            {m.cmsPage_kind_story()}
                        </SelectItem>
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={reset(setStatus)}>
                    <SelectTrigger
                        className="w-44"
                        aria-label={m.cmsPost_filter_status()}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>
                            {m.cmsPost_filter_all_status()}
                        </SelectItem>
                        <SelectItem value="published">
                            {m.cmsPost_status_published()}
                        </SelectItem>
                        <SelectItem value="draft">
                            {m.cmsPost_status_draft()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.cmsPost_title()}</TableHead>
                            <TableHead>{m.cmsPage_filter_kind()}</TableHead>
                            <TableHead>{m.cmsPost_status()}</TableHead>
                            <TableHead>{m.cmsPost_updated_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isError && (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="text-destructive"
                                    role="alert"
                                >
                                    {m.cmsPage_load_failed()}
                                </TableCell>
                            </TableRow>
                        )}
                        {!query.isLoading &&
                            !query.isError &&
                            rows.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        className="text-muted-foreground"
                                    >
                                        {m.cmsPage_empty()}
                                    </TableCell>
                                </TableRow>
                            )}
                        {rows.map((p) => (
                            <TableRow key={p.id}>
                                <TableCell>
                                    <Link
                                        to="/retail/pages/$pageId"
                                        params={{ pageId: p.id }}
                                        className="font-medium text-primary hover:underline"
                                    >
                                        {p.title}
                                    </Link>
                                    <p className="text-xs text-muted-foreground">
                                        /{p.slug}
                                    </p>
                                </TableCell>
                                <TableCell>{pageKindLabel(p.kind)}</TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            p.status === 'published'
                                                ? 'default'
                                                : 'secondary'
                                        }
                                    >
                                        {postStatusLabel(p.status, p.has_draft)}
                                    </Badge>
                                </TableCell>
                                <TableCell>{fmtDate(p.updated_at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                    >
                        {m.cmsPost_prev()}
                    </Button>
                    <span className="text-sm">
                        {m.cmsPost_page({
                            page: String(page),
                            total: String(totalPages),
                        })}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                    >
                        {m.cmsPost_next()}
                    </Button>
                </div>
            )}
        </div>
    )
}
